import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
// import { stat } from "fs";
import sendServerLogging from "../../../utils/sendServerLogging";
import userJSON from "../../../utils/userJSON";
import useFocusTrap from "../../../hooks/useFocusTrap";
import { createIcd10Search } from "./icd10Search";

export default function AddCodeModal({
  showAddCode,
  setShowAddCode,
  setCodesChange,
  selectedCodes,
  setSelectedCodes,
  setIsPrimaryCode,
  setIsSecondaryCode,
  isPrimaryCode,
  isSecondaryCode,
  status
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const listboxRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [filteredCodes, setFilteredCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [fuseObject, setFuseObject] = useState(null);
  const [warningMessage, setWarningMessage] = useState("");
  const listboxId = "icd10-search-listbox";
  const selectedOptionIndex = useMemo(() => {
    if (!selectedCode) return -1;
    return filteredCodes.findIndex(
      (code) =>
        code?.code === selectedCode?.code &&
        (code?.long || "") === (selectedCode?.long || "")
    );
  }, [filteredCodes, selectedCode]);

  // Load data once, build Fuse
  useEffect(() => {
    if (!showAddCode) return;
    async function loadData() {
      try {
        const response = await fetch("/json/ICD10Data.json");
        const data = await response.json();
        const fuse = createIcd10Search(data);
        setFuseObject(fuse);
        setFilteredCodes([]);
      } catch (error) {
        console.error("Failed to load ICD-10 data:", error);
      }
    }
    loadData();
  }, [showAddCode]);

  // Debounce the user’s raw searchQuery
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 300); // wait 300ms

    // Cleanup the timeout if the user keeps typing
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  // Run the actual search on debouncedQuery instead of searchQuery
  useEffect(() => {
    if (!fuseObject) return;

    if (!debouncedQuery) {
      setFilteredCodes([]);
      setWarningMessage("");
      return;
    }

    // Search
    const results = fuseObject.search(debouncedQuery, { limit: 50 });
    let items = results.map((r) => r.item);

    // Filter out unspecified codes and handle warnings
    const queryUpper = debouncedQuery.toUpperCase();
    const blockedTerms = [
      "D509",
      "D649",
      "ANEMIA, UNSPECIFIED",
      "IRON DEFICIENCY ANEMIA, UNSPECIFIED",
    ];

    // Remove from search results
    items = items.filter(
      (icd) =>
        icd.code.toUpperCase() !== "D509" && icd.code.toUpperCase() !== "D649"
    );

    // If exact match is blocked
    if (blockedTerms.includes(queryUpper)) {
      setWarningMessage(
        "Not recommended to add unspecified codes, please search for a more specific code."
      );
    } else {
      setWarningMessage("");
    }

    setFilteredCodes(items);
    setActiveIndex(-1);
  }, [debouncedQuery, fuseObject]);

  const handleClose = () => {
    setShowAddCode(false);
    setSearchQuery("");
    setDebouncedQuery("");
    setFilteredCodes([]);
    setSelectedCode(null);
    setActiveIndex(-1);
    setWarningMessage("");
  };

  function insertPeriodAfterThirdChar(codeString) {
    if (codeString.length <= 3) {
      return codeString;
    }
    return codeString.slice(0, 3) + "." + codeString.slice(3);
  }

  const handleAdd = () => {
    if (!selectedCode) return;
    const formattedCode = insertPeriodAfterThirdChar(selectedCode.code);
    const codeType = isPrimaryCode ? "Primary" : "Secondary";

    const newCodeObject = {
      code: formattedCode,
      name: selectedCode.long || "",
      codeType: codeType,
    };

    const updatedCodesArray = [...(selectedCodes?.codes || []), newCodeObject];
    const updatedCodes = { ...selectedCodes, codes: updatedCodesArray };
    setSelectedCodes(updatedCodes);
    setCodesChange(true);
    setIsPrimaryCode(false);
    setIsSecondaryCode(false);
    handleClose();

    //server logging 
    sendServerLogging({
      body: {
        ...userJSON(localStorage.getItem("userName")),
        event: "ADD_ICD_10_CODE",
        eventData: {
          icd_10_code: selectedCode.long,
          Status: status
        },
      },
    });
  };

  useFocusTrap(showAddCode, dialogRef, null, handleClose);

  if (!showAddCode) {
    return null;
  }

  const modal = (
    <div
      data-testid="add-icd10-modal-overlay"
      className="fixed bg-gray-800 bg-opacity-80 inset-0 flex items-center justify-center z-[1070]"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-code-modal-title"
        className="relative flex flex-col items-center bg-white p-8 rounded-lg shadow-lg w-[80vw] max-w-[600px]"
      >
        <button
          ref={closeButtonRef}
          type="button"
          aria-label="Close add ICD-10 code modal"
          onClick={handleClose}
          className="text-lg absolute top-3 right-5 font-extrabold text-accent1"
        >
          X
        </button>
        <h2 id="add-code-modal-title" className="text-2xl text-accent1 font-bold mt-4 mb-2">
          Add ICD-10 Code
        </h2>
        <p className="text-accent1 mb-4">Search for a code below:</p>

        <div className="relative w-full mb-4">
          <input
            type="text"
            role="combobox"
            aria-label="Search ICD-10 codes"
            aria-expanded={filteredCodes.length > 0}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              activeIndex >= 0
                ? `${listboxId}-option-${activeIndex}`
                : selectedOptionIndex >= 0
                  ? `${listboxId}-option-${selectedOptionIndex}`
                  : undefined
            }
            placeholder="Type search..."
            className="focus:placeholder-white border-0 text-light-grey
                       rounded-lg shadow-knowtex-shadow pl-10 pr-4 py-2 w-full"
            value={searchQuery}
            onFocus={() => {
              if (selectedCode && filteredCodes.length > 0) {
                const idx = selectedOptionIndex;
                if (idx >= 0) setActiveIndex(idx);
              }
            }}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedCode(null);
            }}
            onKeyDown={(e) => {
              if (filteredCodes.length === 0) return;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((prev) => {
                  const next = prev < filteredCodes.length - 1 ? prev + 1 : 0;
                  const el = document.getElementById(`${listboxId}-option-${next}`);
                  el?.scrollIntoView({ block: "nearest" });
                  return next;
                });
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((prev) => {
                  const next = prev > 0 ? prev - 1 : filteredCodes.length - 1;
                  const el = document.getElementById(`${listboxId}-option-${next}`);
                  el?.scrollIntoView({ block: "nearest" });
                  return next;
                });
              } else if (e.key === "Enter" && activeIndex >= 0) {
                e.preventDefault();
                setSelectedCode(filteredCodes[activeIndex]);
              } else if (e.key === "Escape" && filteredCodes.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                setFilteredCodes([]);
                setSearchQuery("");
                setDebouncedQuery("");
                setActiveIndex(-1);
              }
            }}
          />
          <div className="absolute inset-y-0 left-3 flex items-center">
            <svg
              className="w-5 h-5 text-light-grey"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
        </div>

        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {debouncedQuery.length > 1
            ? filteredCodes.length === 0
              ? "No results found"
              : `${filteredCodes.length} result${filteredCodes.length === 1 ? "" : "s"} found`
            : ""}
        </div>
        <div aria-live="assertive" aria-atomic="true" className="sr-only">
          {activeIndex >= 0 && filteredCodes[activeIndex]
            ? `${filteredCodes[activeIndex].code}: ${filteredCodes[activeIndex].long}, ${activeIndex + 1} of ${filteredCodes.length}${selectedCode?.code === filteredCodes[activeIndex].code && (selectedCode?.long || "") === (filteredCodes[activeIndex].long || "") ? ", selected" : ""}`
            : ""}
        </div>
        <div aria-live="assertive" aria-atomic="true" className="sr-only">
          {selectedCode
            ? `Selected: ${selectedCode.code}: ${selectedCode.long}`
            : ""}
        </div>

        {warningMessage && (
          <div role="alert" className="text-red-500 text-sm font-semibold mb-2">
            {warningMessage}
          </div>
        )}

        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label="ICD-10 code search results"
          className="w-full max-h-60 overflow-auto border border-gray-300 rounded-md"
        >
          {filteredCodes.length === 0 && debouncedQuery.length > 1 && (
            <div className="p-2 text-accent1">No results found.</div>
          )}
          {filteredCodes.map((icd, idx) => (
            <div
              data-testid="icd-search-result"
              id={`${listboxId}-option-${idx}`}
              role="option"
              aria-selected={
                selectedCode?.code === icd.code &&
                (selectedCode?.long || "") === (icd?.long || "")
              }
              key={icd.code + "-" + idx}
              className={`p-2 cursor-pointer
              ${
                selectedCode?.code === icd.code &&
                (selectedCode?.long || "") === (icd?.long || "")
                  ? "bg-accent2 text-white hover:bg-purple-800"
                  : activeIndex === idx
                    ? "bg-purple-100 text-accent1 hover:bg-purple-200"
                    : "bg-white text-accent1 hover:bg-gray-100"
              }`}
              onClick={() => setSelectedCode(icd)}
            >
              {icd.code}: {icd.long}
              {selectedCode?.code === icd.code &&
                (selectedCode?.long || "") === (icd?.long || "") &&
                " (selected)"}
            </div>
          ))}
        </div>

        <button
          id="add-code-confirm-button"
          className="bg-accent2 hover:bg-purple2 text-white py-2 px-4 rounded mt-4"
          onClick={handleAdd}
          disabled={!selectedCode}
        >
          Add
        </button>
      </div>
    </div>
  );

  return typeof document === "undefined" ? modal : createPortal(modal, document.body);
}
